import React from 'react';
import {Breadcrumbs, Link, Typography, styled} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import AurIcon, {IconColors, IconNames} from '../aur-icon/AurIcon';
import AurButton from '../aur-button/AurButton';
import {t} from 'i18next';

// Styled component for Breadcrumbs
const StyledBreadcrumbs = styled(Breadcrumbs)(() => ({
  textAlign: 'center',
  '& .MuiBreadcrumbs-separator': {
    margin: '0px 1px',
  },
}));

const IconWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

interface BreadcrumbProps {
  items: Array<{
    label: string;
    path?: string;
    onClick?: (
      e?:
        | React.MouseEvent<HTMLButtonElement, MouseEvent>
        | React.MouseEvent<HTMLAnchorElement>
    ) => void;
    labelColor?: string;
  }>;
}

const AurBreadcrumb: React.FC<BreadcrumbProps> = ({items}) => {
  const navigate = useNavigate();

  return (
    <div className="aur-breadcrumb-container">
      {items.length === 1 && (
        <AurButton
          classNames="aur-breadcrumb-prev-icon"
          ariaLabel={t('indexSearchPage.back')}
          type="text"
          onClick={items?.[0]?.onClick ?? (() => navigate(-1))}
          children={
            <IconWrapper aria-hidden="true">
              <AurIcon
                icon={IconNames.ChevronLeft}
                color={IconColors.Primary}
                size="small"
              />
            </IconWrapper>
          }
          size="Small"
          minWidth={0}
          dataTestId="aur-breadcrumb-prev-button"
        />
      )}
      <StyledBreadcrumbs
        aria-label={
          items?.length
            ? `${t('common.breadcrumb')}-${items?.[items.length - 1]?.label}`
            : t('common.breadcrumb')
        }
        role="navigation"
        separator={
          <IconWrapper aria-hidden="true">
            <AurIcon
              icon={IconNames.ChevronRight}
              color={IconColors.Secondary}
              size="small"
            />
          </IconWrapper>
        }
      >
        {items.map((item, index) => (
          <React.Fragment key={item.label}>
            {item.path ? (
              <Link
                data-testid={`link-testid-${index}`}
                href={item.path}
                color="inherit"
                underline="hover"
                sx={{
                  '&:hover': {
                    color: '#89A6FF',
                    textDecoration: 'underline',
                    textDecorationThickness: '2px',
                    textDecorationColor: '#89A6FF',
                    textUnderlineOffset: '5px',
                  },
                }}
                title={item.label}
                onClick={item.onClick}
              >
                <span
                  id={`${t('common.aurBreadCrumb.breadCrumbLinkId')} ${item.label}`}
                  className="sr-only"
                >
                  {`${t('common.aurBreadCrumb.breadCrumbLinkText')} ${item.label}`}
                </span>
                <Typography
                  sx={{color: item.labelColor ?? '#89A6FF'}}
                  className="typo-text buttonSmall_size button-text"
                >
                  {item.label}
                </Typography>
              </Link>
            ) : (
              <Typography
                sx={{color: item.labelColor ?? '#C9D1DC'}}
                className="typo-text buttonSmall_size button-text"
                aria-disabled={true}
                tabIndex={0}
              >
                {item.label}
              </Typography>
            )}
          </React.Fragment>
        ))}
      </StyledBreadcrumbs>
    </div>
  );
};

export default AurBreadcrumb;
